/**
 * No job left open — the question (mockup sections 1, 2a, 2b:
 * https://claude.ai/artifact/N2GjpWasJBsSGai9Xb3B2Q).
 *
 * Two huge buttons, Coming back / Can't finish. No Hide: the only ways out are
 * answering, "I'm still here" (only while GPS puts him on site, or once for 30
 * minutes when GPS is off) and "All done → Submit" (only when every device has
 * a result). Everything is one tap; nothing to type.
 */
import { useMemo, useState } from "react";
import { Ban, CalendarClock, Check, ChevronLeft, MapPin } from "lucide-react";

import {
    COMING_BACK_REASONS,
    DEVICE_REASONS,
    allHaveResult,
    bookedLabel,
    doneCount,
    jobTitle,
    testedCount,
    type ComingBackReason,
    type DeviceDot,
    type DeviceReason,
    type LeaveAnswerBody,
    type LeaveWhen,
    type LeftOpenJob,
} from "@/lib/left-open";
import { cn } from "@/lib/utils";

type Screen = "question" | "coming_back" | "cant_finish";

export interface LeftOpenPromptProps {
    job: LeftOpenJob;
    /** Why it opened — only changes the headline. */
    trigger: "left_site" | "app_open" | "on_my_way" | "push" | "office_request";
    /** Open straight on one answer (the Today card's buttons). */
    initialScreen?: "coming_back" | "cant_finish";
    /** True = GPS puts him on this site now; false = off site; null = no GPS. */
    onSite: boolean | null;
    /** "I'm still here" is offered (on site, or GPS off and not used in the last 30 min). */
    canStayHere: boolean;
    busy: boolean;
    onStillHere: () => void;
    onAllDone: () => void;
    onAnswer: (body: Omit<LeaveAnswerBody, "tappedAt">) => void;
    /**
     * Clean start: the office answers a From before job on the Left open list
     * with these same screens. No "Later today" (the tech isn't coming back
     * today on an old job), and tested devices are filed from the job page.
     */
    officeMode?: boolean;
    /** Office mode: close without answering. */
    onCancel?: () => void;
}

export function LeftOpenPrompt(props: LeftOpenPromptProps) {
    const [screen, setScreen] = useState<Screen>(props.initialScreen ?? "question");
    // The office opens one answer straight from its card, so Back closes it.
    const back = () => (props.officeMode && props.onCancel ? props.onCancel() : setScreen("question"));
    return (
        <div
            className="fixed inset-0 z-[80] flex items-stretch justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="left-open-title"
            data-testid="left-open-prompt"
        >
            <div className="flex w-full max-w-4xl flex-col overflow-y-auto bg-card text-foreground shadow-2xl sm:max-h-[94vh] sm:rounded-3xl">
                {screen === "question" && <Question {...props} onPick={setScreen} />}
                {screen === "coming_back" && <ComingBack {...props} onBack={back} />}
                {screen === "cant_finish" && <CantFinish {...props} onBack={back} />}
            </div>
        </div>
    );
}

// ── Dots ───────────────────────────────────────────────────────────────────

const DOT_CLASS: Record<DeviceDot, string> = {
    pass: "bg-emerald-700 text-white",
    fail: "bg-red-700 text-white",
    todo: "border-[3px] border-dashed border-slate-400 dark:border-slate-500",
    gone: "bg-violet-700 text-white",
    cant: "bg-amber-700 text-white",
};
const DOT_GLYPH: Record<DeviceDot, string> = { pass: "✓", fail: "✕", todo: "", gone: "–", cant: "!" };
const DOT_WORD: Record<DeviceDot, string> = {
    pass: "passed",
    fail: "failed",
    todo: "not done",
    gone: "gone",
    cant: "can't test",
};

export function DeviceDots({ dots, size = 26 }: { dots: DeviceDot[]; size?: number }) {
    return (
        <span
            className="inline-flex flex-wrap items-center gap-1.5"
            aria-label={dots.map((d) => DOT_WORD[d]).join(", ")}
        >
            {dots.map((d, i) => (
                <span
                    key={i}
                    className={cn("inline-grid place-items-center rounded-full font-bold", DOT_CLASS[d])}
                    style={{ width: size, height: size, fontSize: Math.round(size * 0.58) }}
                    aria-hidden
                >
                    {DOT_GLYPH[d]}
                </span>
            ))}
        </span>
    );
}

// ── 1. The question ────────────────────────────────────────────────────────

function headline(p: LeftOpenPromptProps): string {
    const name = jobTitle(p.job);
    if (p.trigger === "left_site" || p.onSite === false) return `You left ${name}`;
    return `${name} is still open`;
}

function Question(p: LeftOpenPromptProps & { onPick: (s: Screen) => void }) {
    const dots = p.job.dots;
    const left = dots.filter((d) => d === "todo").length;
    return (
        <div className="flex min-h-full flex-col gap-5 p-5 sm:p-7">
            <div className="flex items-center gap-4">
                <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-amber-100 dark:bg-amber-900/50">
                    <MapPin className="h-7 w-7 text-amber-800 dark:text-amber-300" aria-hidden />
                </div>
                <div className="min-w-0 space-y-1.5">
                    <h2 id="left-open-title" className="text-2xl font-bold leading-tight sm:text-3xl">
                        {headline(p)}
                    </h2>
                    {dots.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3">
                            <DeviceDots dots={dots} />
                            <span className="text-lg text-muted-foreground">
                                {doneCount(dots)} of {dots.length} done
                            </span>
                        </div>
                    )}
                    {p.trigger === "on_my_way" && (
                        <p className="text-base text-muted-foreground">Answer this before you head to the next job.</p>
                    )}
                </div>
            </div>

            <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
                <BigButton
                    tone="go"
                    icon={<CalendarClock className="h-16 w-16" aria-hidden />}
                    label="Coming back"
                    hint={
                        left > 0
                            ? `Someone needs to go back to finish ${left === 1 ? "this 1" : `these ${left}`}`
                            : "Someone needs to go back"
                    }
                    onClick={() => p.onPick("coming_back")}
                    disabled={p.busy}
                    testId="left-open-coming-back"
                />
                <BigButton
                    tone="stop"
                    icon={<Ban className="h-16 w-16" aria-hidden />}
                    label="Can't finish"
                    hint={
                        left > 0
                            ? `${left === 1 ? "This 1 can't" : `These ${left} can't`} be tested. Tell the office why.`
                            : "Tell the office why."
                    }
                    onClick={() => p.onPick("cant_finish")}
                    disabled={p.busy}
                    testId="left-open-cant-finish"
                />
            </div>

            <div className="flex flex-wrap justify-center gap-6 pb-1 text-lg">
                {p.canStayHere && (
                    <button
                        type="button"
                        className="border-b-2 border-border pb-0.5 text-muted-foreground hover:text-foreground"
                        onClick={p.onStillHere}
                        disabled={p.busy}
                        data-testid="left-open-still-here"
                    >
                        I'm still here
                    </button>
                )}
                {allHaveResult(dots) && (
                    <button
                        type="button"
                        className="border-b-2 border-border pb-0.5 text-muted-foreground hover:text-foreground"
                        onClick={p.onAllDone}
                        disabled={p.busy}
                        data-testid="left-open-all-done"
                    >
                        All done → Submit
                    </button>
                )}
            </div>
        </div>
    );
}

function BigButton(props: {
    tone: "go" | "stop";
    icon: React.ReactNode;
    label: string;
    hint: string;
    onClick: () => void;
    disabled?: boolean;
    testId: string;
}) {
    return (
        <button
            type="button"
            onClick={props.onClick}
            disabled={props.disabled}
            data-testid={props.testId}
            className={cn(
                "flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-2xl border-[3px] p-4 text-center transition-colors",
                props.tone === "go"
                    ? "border-blue-700 bg-blue-50 text-blue-800 hover:bg-blue-100 dark:border-blue-400 dark:bg-blue-950/60 dark:text-blue-200 dark:hover:bg-blue-950"
                    : "border-amber-700 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-400 dark:bg-amber-950/60 dark:text-amber-200 dark:hover:bg-amber-950",
            )}
        >
            {props.icon}
            <span className="text-3xl font-bold">{props.label}</span>
            <span className="text-base">{props.hint}</span>
        </button>
    );
}

// ── Shared bits ────────────────────────────────────────────────────────────

function SubHeader({ job, onBack, busy }: { job: LeftOpenJob; onBack: () => void; busy: boolean }) {
    return (
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            <button
                type="button"
                onClick={onBack}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-base font-medium text-muted-foreground hover:bg-secondary"
            >
                <ChevronLeft className="h-5 w-5" aria-hidden /> Back
            </button>
            <span id="left-open-title" className="min-w-0 truncate text-base font-semibold">
                {jobTitle(job)}
                {job.displayNumber ? (
                    <span className="font-mono text-muted-foreground"> · #{job.displayNumber}</span>
                ) : null}
            </span>
        </div>
    );
}

function Chip(props: {
    on: boolean;
    tone: "go" | "stop" | "gone";
    onClick: () => void;
    children: React.ReactNode;
    className?: string;
    testId?: string;
}) {
    const onClass =
        props.tone === "go"
            ? "border-blue-700 bg-blue-50 text-blue-800 dark:border-blue-400 dark:bg-blue-950/60 dark:text-blue-200"
            : props.tone === "stop"
              ? "border-amber-700 bg-amber-50 text-amber-900 dark:border-amber-400 dark:bg-amber-950/60 dark:text-amber-200"
              : "border-violet-700 bg-card text-violet-800 dark:border-violet-400 dark:text-violet-200";
    return (
        <button
            type="button"
            onClick={props.onClick}
            aria-pressed={props.on}
            data-testid={props.testId}
            className={cn(
                "flex items-center justify-center gap-2 rounded-xl border-2 px-3 text-center font-bold transition-colors",
                props.on ? onClass : "border-border bg-background text-muted-foreground hover:bg-secondary",
                props.className,
            )}
        >
            {props.children}
        </button>
    );
}

function Cta(props: { tone: "go" | "stop"; disabled: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            type="button"
            disabled={props.disabled}
            onClick={props.onClick}
            data-testid="left-open-send"
            className={cn(
                "mx-4 mb-4 mt-auto h-16 rounded-2xl text-xl font-bold text-white transition-colors disabled:cursor-not-allowed disabled:bg-slate-500",
                props.tone === "go" ? "bg-blue-700 hover:bg-blue-800" : "bg-amber-700 hover:bg-amber-800",
            )}
        >
            {props.children}
        </button>
    );
}

// ── 2a. Coming back ────────────────────────────────────────────────────────

function ComingBack(p: LeftOpenPromptProps & { onBack: () => void }) {
    const [reason, setReason] = useState<ComingBackReason | null>(null);
    const [when, setWhen] = useState<LeaveWhen | null>(null);
    const [gone, setGone] = useState<Set<string>>(new Set());
    const todo = p.job.devices.filter((d) => d.dot === "todo");
    const left = todo.length - gone.size;
    const toggle = (id: string) =>
        setGone((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    const cta =
        when === "another_day"
            ? `Send to scheduling${todo.length ? ` · ${left} left` : ""}`
            : when === "later_today"
              ? "I'll be back today"
              : when === "booked"
                ? "Keep it for the booked visit"
                : "Pick why and when";
    return (
        <div className="flex min-h-full flex-col">
            <SubHeader job={p.job} onBack={p.onBack} busy={p.busy} />
            <div className="space-y-5 p-4">
                <section className="space-y-2">
                    <h3 className="text-lg font-bold">
                        {p.officeMode ? "Why is it still open?" : "Why are you leaving?"}
                    </h3>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                        {COMING_BACK_REASONS.map((r) => (
                            <Chip
                                key={r.value}
                                tone="go"
                                on={reason === r.value}
                                onClick={() => setReason(r.value)}
                                className="h-20 text-base"
                                testId={`left-open-why-${r.value}`}
                            >
                                {r.label}
                            </Chip>
                        ))}
                    </div>
                </section>
                <section className="space-y-2">
                    <h3 className="text-lg font-bold">When?</h3>
                    <div
                        className={cn(
                            "grid gap-3",
                            (p.job.laterBookedStart ? 1 : 0) + (p.officeMode ? 1 : 2) === 3
                                ? "sm:grid-cols-3"
                                : "sm:grid-cols-2",
                        )}
                    >
                        {!p.officeMode && (
                            <Chip
                                tone="go"
                                on={when === "later_today"}
                                onClick={() => setWhen("later_today")}
                                className="h-16 text-base"
                                testId="left-open-when-later_today"
                            >
                                Later today
                            </Chip>
                        )}
                        <Chip
                            tone="go"
                            on={when === "another_day"}
                            onClick={() => setWhen("another_day")}
                            className="h-16 text-base"
                            testId="left-open-when-another_day"
                        >
                            Another day · office books it
                        </Chip>
                        {p.job.laterBookedStart && (
                            <Chip
                                tone="go"
                                on={when === "booked"}
                                onClick={() => setWhen("booked")}
                                className="h-16 text-base"
                                testId="left-open-when-booked"
                            >
                                {bookedLabel(p.job.laterBookedStart)}
                            </Chip>
                        )}
                    </div>
                </section>
                {todo.length > 0 && (
                    <section className="space-y-2">
                        <div className="flex items-baseline justify-between gap-3">
                            <h3 className="text-lg font-bold">Left to do</h3>
                            <span className="text-sm text-muted-foreground">Tap one if it's gone</span>
                        </div>
                        <div className="flex flex-wrap gap-2.5">
                            {todo.map((d) => (
                                <Chip
                                    key={d.linkId}
                                    tone="gone"
                                    on={gone.has(d.linkId)}
                                    onClick={() => toggle(d.linkId)}
                                    className="h-12 px-3 text-sm"
                                    testId={`left-open-gone-${d.linkId}`}
                                >
                                    <DeviceDots dots={[gone.has(d.linkId) ? "gone" : "todo"]} size={18} />
                                    {d.label}
                                    {gone.has(d.linkId) ? " · gone" : ""}
                                </Chip>
                            ))}
                        </div>
                    </section>
                )}
            </div>
            <Cta
                tone="go"
                disabled={p.busy || !reason || !when}
                onClick={() =>
                    reason &&
                    when &&
                    p.onAnswer({
                        choice: "coming_back",
                        reason,
                        when,
                        devices: [...gone].map((linkId) => ({ linkId, reason: "device_gone" as const })),
                    })
                }
            >
                {cta}
            </Cta>
        </div>
    );
}

// ── 2b. Can't finish ───────────────────────────────────────────────────────

function CantFinish(p: LeftOpenPromptProps & { onBack: () => void }) {
    const todo = useMemo(() => p.job.devices.filter((d) => d.dot === "todo"), [p.job.devices]);
    const [picked, setPicked] = useState<Record<string, DeviceReason>>({});
    const [sameForAll, setSameForAll] = useState(false);
    const tested = testedCount(p.job.dots);
    const complete = todo.every((d) => picked[d.linkId]);
    const pick = (linkId: string, r: DeviceReason) =>
        setPicked((m) => {
            if (!sameForAll) return { ...m, [linkId]: r };
            const all: Record<string, DeviceReason> = {};
            for (const d of todo) all[d.linkId] = r;
            return all;
        });
    return (
        <div className="flex min-h-full flex-col">
            <SubHeader job={p.job} onBack={p.onBack} busy={p.busy} />
            <div className="space-y-3 p-4">
                <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-lg font-bold">Why couldn't you do it?</h3>
                    {todo.length > 1 && (
                        <button
                            type="button"
                            onClick={() => {
                                setSameForAll((v) => !v);
                                const first = todo.map((d) => picked[d.linkId]).find(Boolean);
                                if (!sameForAll && first) {
                                    const all: Record<string, DeviceReason> = {};
                                    for (const d of todo) all[d.linkId] = first;
                                    setPicked(all);
                                }
                            }}
                            aria-pressed={sameForAll}
                            className={cn(
                                "rounded-lg px-2 py-1 text-base font-bold",
                                sameForAll ? "bg-amber-700 text-white" : "text-amber-800 dark:text-amber-300",
                            )}
                            data-testid="left-open-same-for-all"
                        >
                            Same for all
                        </button>
                    )}
                </div>
                {todo.length === 0 && (
                    <p className="text-base text-muted-foreground">
                        Every device has a result. The office gets one task.
                    </p>
                )}
                {todo.map((d) => (
                    <div
                        key={d.linkId}
                        className="grid gap-3 rounded-2xl border border-border bg-background p-3 md:grid-cols-[180px_1fr] md:items-center"
                    >
                        <div className="min-w-0">
                            <div className="text-base font-bold leading-tight">{d.label}</div>
                            {d.location && <div className="text-sm text-muted-foreground">{d.location}</div>}
                        </div>
                        <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
                            {DEVICE_REASONS.map((r) => (
                                <Chip
                                    key={r.value}
                                    tone="stop"
                                    on={picked[d.linkId] === r.value}
                                    onClick={() => pick(d.linkId, r.value)}
                                    className="h-14 text-sm"
                                    testId={`left-open-device-${d.linkId}-${r.value}`}
                                >
                                    {picked[d.linkId] === r.value && <Check className="h-4 w-4" aria-hidden />}
                                    {r.label}
                                </Chip>
                            ))}
                        </div>
                    </div>
                ))}
                <p className="px-1 text-base text-muted-foreground">
                    {p.officeMode
                        ? tested > 0
                            ? `Tested devices: ${tested}. File those from the job page.`
                            : "Tested devices: none. It goes to office review."
                        : tested > 0
                          ? `Tested devices: ${tested}. You'll submit those next (signature and all). The office gets 1 task.`
                          : "Tested devices: none. Nothing to submit. The office gets 1 task."}
                </p>
            </div>
            <Cta
                tone="stop"
                disabled={p.busy || !complete}
                onClick={() =>
                    p.onAnswer({
                        choice: "cant_finish",
                        devices: todo.map((d) => ({ linkId: d.linkId, reason: picked[d.linkId]! })),
                        ...(todo.length === 0 ? { reason: "other" as const } : {}),
                    })
                }
            >
                {p.officeMode
                    ? "Save answer"
                    : tested > 0
                      ? `Submit ${tested} tested · send to office`
                      : "Send to office"}
            </Cta>
        </div>
    );
}
